import { useState } from "react";
import { listEmailVersions, saveEmailDraft, publishEmail, EmailVersion } from "../../../../services/EmailSerivce";

export interface EmailVersionActionsOptions {
	selectedId: number | null;
	templateName: string;
	templateSubject: string;
	onPublishSuccess?: () => void;
	onRestoreSuccess?: (version: EmailVersion) => void;
	setSaveAlert: (msg: string) => void;
	/** Show a transient success message (e.g. "Đã lưu nháp"). */
	notify?: (msg: string) => void;
}

export interface EmailVersionActions {
	handleSaveDraft: (content: string) => Promise<void>;
	handlePublish: (content: string) => Promise<void>;
	handleListVersions: () => Promise<EmailVersion[]>;
	handleRestore: (versionId: number) => Promise<void>;
	draftLoading: boolean;
	publishLoading: boolean;
}

export function useEmailVersionActions({
	selectedId,
	templateName,
	templateSubject,
	onPublishSuccess,
	onRestoreSuccess,
	setSaveAlert,
	notify,
}: EmailVersionActionsOptions): EmailVersionActions {
	const [draftLoading, setDraftLoading] = useState(false);
	const [publishLoading, setPublishLoading] = useState(false);

	const id = selectedId || 0;

	const handleSaveDraft = async (content: string) => {
		if (!id) return;
		setDraftLoading(true);
		try {
			await saveEmailDraft(id, {
				content,
				name: templateName || undefined,
				subject: templateSubject || undefined,
			});
			setSaveAlert("");
			notify?.("Đã lưu nháp ✓");
		} catch (_e) {
			setSaveAlert("Lưu nháp thất bại. Vui lòng thử lại.");
		} finally {
			setDraftLoading(false);
		}
	};

	const handlePublish = async (content: string) => {
		if (!id) return;
		setPublishLoading(true);
		try {
			await publishEmail(id, { content, name: templateName || undefined, subject: templateSubject || undefined });
			setSaveAlert("");
			notify?.("Đã xuất bản ✓");
			try {
				onPublishSuccess?.();
			} catch {
				/* post-publish refresh failed; the publish itself already succeeded, so do not show an error */
			}
		} catch (_e) {
			setSaveAlert("Xuất bản thất bại. Vui lòng thử lại.");
		} finally {
			setPublishLoading(false);
		}
	};

	const handleListVersions = async (): Promise<EmailVersion[]> => {
		if (!id) return [];
		return listEmailVersions(id);
	};

	const handleRestore = async (versionId: number) => {
		if (!id) return;
		try {
			// Restore chỉ TẢI nội dung phiên bản vào trình soạn thảo — không tạo
			// version mới, không tự xuất bản. User xem lại rồi tự bấm "Xuất bản".
			const versions = await listEmailVersions(id);
			const target = versions.find((v) => v.id === versionId);
			if (!target) {
				setSaveAlert("Không tìm thấy phiên bản. Vui lòng thử lại.");
				return;
			}
			onRestoreSuccess?.(target);
			setSaveAlert("");
			notify?.("Đã tải nội dung phiên bản vào trình soạn — bấm Xuất bản để áp dụng ✓");
		} catch (_e) {
			setSaveAlert("Khôi phục thất bại. Vui lòng thử lại.");
		}
	};

	return { handleSaveDraft, handlePublish, handleListVersions, handleRestore, draftLoading, publishLoading };
}
